import React, { Suspense, lazy } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import {
  AuthenticatedAppSpinner,
  PublicLayoutSpinner,
} from 'components/V1/Spinner/Spinner';
import { ErrorFallback } from 'components/V1/ErrorFallback/ErrorFallback';
import { useAuth } from 'context/AuthContext/AuthContext';
import Layout from 'views/Layout/Layout';
import PublicLayout from 'views/PublicLayout/PublicLayout';
import { V2Switch } from 'components/V2/V2Switch';
import PublicLayoutV2 from 'views/PublicLayoutV2/PublicLayoutV2';
import useTrackIdentity from 'utils/useTrackIdentity';
import useIntercomSync from 'utils/useIntercomSync';
import { FeatureSwitch } from 'components/V1/Feature/Feature';

const Overview = lazy(
  () => import(/* webpackPrefetch: true */ 'views/Overview/Overview')
);
const OverviewV2 = lazy(() => import('views/Overview/OverviewV2'));
const Payments = lazy(() => import('views/Payments/Payments'));
const PayinDetails = lazy(
  () => import('views/Payins/PayinDetails/PayinDetails')
);
const PayoutDetails = lazy(
  () => import('views/Payouts/PayoutDetails/PayoutDetails')
);
const ItemDetails = lazy(() => import('views/Items/ItemDetails/ItemDetails'));
const User = lazy(() => import('views/Users/User/User'));
const UsersOverview = lazy(
  () => import('views/Users/UsersOverview/UsersOverview')
);
const PlatformProfile = lazy(
  () => import('views/PlatformProfile/PlatformProfile')
);
const GettingStarted = lazy(
  () => import('views/GettingStarted/GettingStarted')
);
const Verification = lazy(() => import('views/Verification/Verification'));
const Reports = lazy(() => import('views/Reports/Reports'));

function AuthenticatedApp() {
  useTrackIdentity();
  useIntercomSync();
  const location = useLocation();
  const { tokens } = useAuth();

  const verification = (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<PublicLayoutSpinner />}>
        <Verification />
      </Suspense>
    </ErrorBoundary>
  );

  return (
    <Switch>
      <Route path="/verification">
        {tokens?.initiate_mfa ? (
          <V2Switch>
            <V2Switch.New>
              <PublicLayoutV2>{verification}</PublicLayoutV2>
            </V2Switch.New>
            <V2Switch.Old>
              <PublicLayout>{verification}</PublicLayout>
            </V2Switch.Old>
          </V2Switch>
        ) : (
          <Redirect to="/" />
        )}
      </Route>
      <Route path="/">
        <Layout>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<AuthenticatedAppSpinner />}>
              <Switch>
                <Route path="/" exact>
                  <V2Switch>
                    <V2Switch.New>
                      <OverviewV2 />
                    </V2Switch.New>
                    <V2Switch.Old>
                      <Overview />
                    </V2Switch.Old>
                  </V2Switch>
                </Route>
                <Route path="/payments">
                  <Switch>
                    {/* Must precede the `:id` routes, or
                        /payments/payins/method/bpay matches id === "method". */}
                    <Route path="/payments/:tab(payins|payouts|items|transfers)/method/:method">
                      <Payments />
                    </Route>
                    <Route path="/payments/payins/:id">
                      <PayinDetails />
                    </Route>
                    <Route path="/payments/payouts/:id">
                      <PayoutDetails />
                    </Route>
                    <Route path="/payments/items/:id">
                      <ItemDetails />
                    </Route>
                    <Route path="/payments/:tab(payins|payouts|items|transfers)">
                      <Payments />
                    </Route>
                    <Redirect to="/payments/payins" />
                  </Switch>
                </Route>
                <Route path="/users/:id">
                  <User />
                </Route>
                <Route path="/users">
                  <UsersOverview />
                </Route>
                <Route path="/platform-profile">
                  <PlatformProfile />
                </Route>
                <Route path="/getting-started">
                  <GettingStarted />
                </Route>
                <Route path="/reports">
                  <FeatureSwitch flag="data-export">
                    <FeatureSwitch.On>
                      <Reports />
                    </FeatureSwitch.On>
                    <FeatureSwitch.Off>
                      <Redirect to="/" />
                    </FeatureSwitch.Off>
                  </FeatureSwitch>
                </Route>
                <Redirect
                  to={{
                    pathname: '/',
                    state: { from: location },
                  }}
                />
              </Switch>
            </Suspense>
          </ErrorBoundary>
        </Layout>
      </Route>
    </Switch>
  );
}

export default AuthenticatedApp;
